import React, { useState } from 'react'

import { Navigate, useNavigate, useParams } from 'react-router-dom'

import { Zone } from '@/core/api/entities/zone/zone'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useLocationByUUID } from '@/core/hooks/useLocationByUUID'
import { usePatchLocationZoneVolume } from '@/core/hooks/usePatchLocationZoneVolume'
import { useTranslation } from '@/core/i18n/useTranslation'
import { ZoneCard } from '@/features/zones/components/ZoneCard'
import { ManageZoneVolumeModal } from '@/features/zones/modals/ManageZoneVolumeModal'
import PageLayout from '@/shared/components/organisms/Layout/PageLayout'
import { useNotificationContext } from '@/shared/context/NotificationProvider'

/**
 * Public/shared page opened by: /locations/:uuid/details
 * It must not require auth.
 */
const LocationMonitor: React.FC = () => {
	const { t } = useTranslation('zones')
	const { t: tCommon } = useTranslation('common')
	const { showNotification } = useNotificationContext()
	const { uuid } = useParams<{ uuid: string }>()
	const navigate = useNavigate()
	const locationUUID = uuid ?? ''
	const accountConfig = useAccountConfig({})

	const [selectedZone, setSelectedZone] = useState<Zone | null>(null)
	const [volumeModalOpen, setVolumeModalOpen] = useState(false)

	const {
		data: location,
		isLoading,
		error,
	} = useLocationByUUID({
		uuid: locationUUID,
		options: {
			enabled: Boolean(uuid),
		},
	})

	const { mutate: patchZoneVolume, isPending: isPatchingVolume } =
		usePatchLocationZoneVolume({
			config: accountConfig,
			options: {
				onSuccess: () => {
					showNotification({
						type: 'success',
						message: tCommon('common.saved'),
					})
					setVolumeModalOpen(false)
					setSelectedZone(null)
				},
				onError: () => {
					showNotification({
						type: 'error',
						message: tCommon('common.try_again_later'),
					})
				},
			},
		})

	// If missing uuid, go somewhere safe.
	if (!uuid) return <Navigate to="/login" replace />

	return (
		<>
			<PageLayout
				requireAuth={false}
				backButton={{
					show: true,
					onClick: () => navigate(-1),
				}}
				title={location?.name || t('location.monitor.locationLabel')}
				contentClassName="px-6 py-6"
			>
				{isLoading ? (
					<div className="text-muted-foreground">{t('location.loading')}</div>
				) : error || !location ? (
					<div className="text-muted-foreground">{t('location.notFound')}</div>
				) : (
					<div className="space-y-6">
						<div className="rounded-xl border bg-card p-4">
							<p className="text-sm text-muted-foreground">
								{t('location.monitor.intro')}
							</p>
							<p className="text-sm text-muted-foreground mt-3">
								{t('location.monitor.howTo')}
							</p>
						</div>

						<div className="rounded-xl border bg-card p-4">
							<p className="text-sm text-muted-foreground">
								{t('location.monitor.locationLabel')}
							</p>
							<p className="text-lg font-semibold">{location.name}</p>
							<p className="text-sm text-muted-foreground mt-2">
								{location.address || ''}
							</p>
						</div>

						<div className="rounded-xl border bg-card p-4">
							<p className="text-sm font-semibold mb-3">{t('tabs.zones')}</p>
							{location.zones?.length ? (
								<div className="space-y-2">
									{location.zones.map((zone) => (
										<ZoneCard
											key={zone.id}
											zone={zone}
											sharedLocationView
											onManageVolume={(z) => {
												setSelectedZone(z)
												setVolumeModalOpen(true)
											}}
										/>
									))}
								</div>
							) : (
								<p className="text-sm text-muted-foreground">
									{t('location.monitor.noZones')}
								</p>
							)}
						</div>
					</div>
				)}
			</PageLayout>

			<ManageZoneVolumeModal
				visible={volumeModalOpen}
				zone={selectedZone}
				onClose={() => {
					setVolumeModalOpen(false)
					setSelectedZone(null)
				}}
				isUpdating={isPatchingVolume}
				onConfirm={(data) => {
					if (!selectedZone) return
					patchZoneVolume({
						locationUUID,
						zoneId: selectedZone.id,
						data,
					})
				}}
			/>
		</>
	)
}

export default LocationMonitor
